import { entityGenerateCtaActionFromUserMessage } from "src/lib/entityGenerateCtaAction";
import { isStoryBibleCtaType } from "~shared/agenticChat/storyBibleCtaTypes";
import type { CTAAction, CTAActionStatus, CTAActionType } from "./ThinkingStep/cta-types";

const DEFAULT_DISPLAY_TITLES: Partial<Record<CTAActionType, string>> = {
  generate_character: "Generate Character",
  generate_worldbuilding: "Generate Element",
  generate_cast: "Generate All Characters",
  generate_synopsis: "Generate Synopsis",
  rewrite_synopsis: "Rewrite Synopsis",
  generate_outline: "Generate Outline",
  rewrite_outline: "Rewrite Outline",
  generate_chapter: "Generate Chapter",
  rewrite_chapter: "Rewrite Chapter",
  generate_trait: "Generate Trait",
  rewrite_trait: "Rewrite Trait",
  generate_worldbuilding_trait: "Generate Trait",
  rewrite_worldbuilding_trait: "Rewrite Trait",
};

type UserMessageForResend = {
  content?: string;
  metadata?: unknown;
};

function parseCtaPayloadFromUserMetadata(metadata: unknown): Record<string, unknown> | null {
  if (!metadata || typeof metadata !== "object") return null;
  const turnContext = (metadata as { turnContext?: unknown }).turnContext;
  if (!turnContext || typeof turnContext !== "object") return null;
  const ctaAction = (turnContext as { ctaAction?: unknown }).ctaAction;
  if (!ctaAction || typeof ctaAction !== "object") return null;
  const type = (ctaAction as { type?: unknown }).type;
  if (typeof type !== "string") return null;
  return ctaAction as Record<string, unknown>;
}

function firstContentLine(content: string | undefined, fallback: string): string {
  const line = typeof content === "string" ? content.split("\n")[0]?.trim() : "";
  return line || fallback;
}

function ctaActionFromPayload(
  type: CTAActionType,
  payload: Record<string, unknown>,
  userMessage: UserMessageForResend,
): CTAAction {
  const displayTitle =
    (typeof payload.displayTitle === "string" && payload.displayTitle.trim())
    || DEFAULT_DISPLAY_TITLES[type]
    || "Story Bible";
  const userLine = firstContentLine(userMessage.content, displayTitle);

  return {
    type,
    status: "in_progress",
    displayTitle,
    userMessage: userLine,
    cannedResponse: "",
    targetSurface: payload.targetSurface as CTAAction["targetSurface"],
    targetId: typeof payload.targetId === "string" ? payload.targetId : undefined,
    targetName: typeof payload.targetName === "string" ? payload.targetName : undefined,
    customInstructions: typeof payload.customInstructions === "string" ? payload.customInstructions : undefined,
    outlineFramework: typeof payload.outlineFramework === "string" ? payload.outlineFramework : undefined,
    characterId: typeof payload.characterId === "string" ? payload.characterId : undefined,
    entryId: typeof payload.entryId === "string" ? payload.entryId : undefined,
    traitId: typeof payload.traitId === "string" ? payload.traitId : undefined,
    traitLabel: typeof payload.traitLabel === "string" ? payload.traitLabel : undefined,
    role: typeof payload.role === "string" ? payload.role : undefined,
    roleLabel: typeof payload.roleLabel === "string" ? payload.roleLabel : undefined,
    roleExplicitlySelected: payload.roleExplicitlySelected === true,
  };
}

function resendStatusForCta(type: CTAActionType, payload: Record<string, unknown> | null): CTAActionStatus {
  if (type === "generate_cast" && payload?.characterCount == null) {
    return "awaiting_input";
  }
  return "in_progress";
}

/**
 * Restore Story Bible CTA chrome for resend so the floating progress card matches the original CTA send.
 * Server replay already uses `metadata.turnContext.ctaAction`; this keeps the client UI in sync.
 */
export function ctaActionForResendFromUserMessage(
  userMessage: UserMessageForResend | null | undefined,
): CTAAction | null {
  if (!userMessage) return null;

  const payload = parseCtaPayloadFromUserMetadata(userMessage.metadata);
  const payloadType = typeof payload?.type === "string" ? payload.type as CTAActionType : undefined;

  if (payloadType && isStoryBibleCtaType(payloadType)) {
    const action = ctaActionFromPayload(payloadType, payload, userMessage);
    return { ...action, status: resendStatusForCta(payloadType, payload) };
  }

  const entityAction = entityGenerateCtaActionFromUserMessage(userMessage);
  if (entityAction) {
    return { ...entityAction, status: "in_progress" };
  }

  const metadataType = (userMessage.metadata as { ctaActionType?: string } | undefined)?.ctaActionType as CTAActionType | undefined;
  if (!metadataType || !isStoryBibleCtaType(metadataType)) {
    return null;
  }

  const displayTitle = DEFAULT_DISPLAY_TITLES[metadataType] ?? "Story Bible";
  return {
    type: metadataType,
    status: "in_progress",
    displayTitle,
    userMessage: firstContentLine(userMessage.content, displayTitle),
    cannedResponse: "",
  };
}
